import * as React from 'react';
import { Flame, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { getHotspots, getHotspotChannels, type HotspotChannel } from '@/api/client';

type Hotspot = {
  title: string;
  url: string;
  source: string;
  pub?: string;
  hot?: number;
  trend?: string;
};

type HotspotSourceStatus = {
  name: string;
  url: string;
  ok: boolean;
  error?: string;
};

type ChannelGroup = { key: string; name: string; members: string[] };

// 平台热榜 Tab 内二级分组（按来源类型）；未匹配的赛道归入「其他」
const PLATFORM_GROUPS: ChannelGroup[] = [
  { key: 'general', name: '综合资讯', members: ['百度热搜', '微博热搜', '今日头条热榜', '腾讯新闻热点'] },
  { key: 'video', name: '视频平台', members: ['抖音热榜', 'B站热门', 'AcFun排行榜'] },
  { key: 'community', name: '社区论坛', members: ['知乎热榜', '百度贴吧热议', 'NGA热帖'] },
  { key: 'tech', name: '科技媒体', members: ['36氪热榜', 'IT之家热榜', '掘金热榜', '少数派热榜', '果壳热文'] },
];

// 行业板块 Tab 内二级分组（按行业大类）
const FIELD_GROUPS: ChannelGroup[] = [
  { key: 'consume', name: '消费文娱', members: ['娱乐', '游戏', '体育', '生活', '汽车'] },
  { key: 'politics', name: '政经民生', members: ['时政', '财经', '房地产', '职场', '法律'] },
  { key: 'eduhealth', name: '教育健康科技', members: ['教育', '健康医疗', '科技'] },
];

const BADGE_MAP: Record<string, { label: string; className: string }> = {
  new: { label: '新', className: 'border-dashed border-violet-400 text-violet-500' },
  ren: { label: '改', className: 'border-dashed border-orange-400 text-orange-500' },
};

const formatHot = (hot?: number) => {
  const v = hot || 0;
  if (v <= 0) return '';
  if (v >= 100000000) return `${(v / 100000000).toFixed(1)}亿`;
  if (v >= 10000) return `${(v / 10000).toFixed(1)}万`;
  return String(Math.round(v));
};

const TrendArrow = ({ trend }: { trend?: string }) => {
  if (!trend || trend === 'flat') return null;
  if (trend === 'up') {
    return (
      <span className="flex items-center gap-0.5 text-[11px] font-bold text-red-500" title="较上小时上升">
        <span className="text-[9px]">▲</span>
        <span className="text-[10px]">升</span>
      </span>
    );
  }
  if (trend === 'down') {
    return (
      <span className="flex items-center gap-0.5 text-[11px] font-bold text-green-600" title="较上小时下降">
        <span className="text-[9px]">▼</span>
        <span className="text-[10px]">降</span>
      </span>
    );
  }
  return (
    <span className="flex items-center text-[11px] font-bold text-sky-500" title="本小时新上榜">
      <span className="text-[10px]">新</span>
    </span>
  );
};

// 热度数据条：相对当前列表最大值归一化，最小值 6% 保证可读
const HotBar = ({ hot, maxHot }: { hot: number; maxHot: number }) => {
  if (!hot || hot <= 0 || maxHot <= 0) return null;
  const pct = Math.max(6, Math.min(100, Math.round((hot / maxHot) * 100)));
  return (
    <span className="h-1 w-12 flex-shrink-0 overflow-hidden rounded-full bg-muted" title={`热度 ${formatHot(hot)}`}>
      <span className="block h-full rounded-full bg-primary" style={{ width: `${pct}%` }} />
    </span>
  );
};

export default function HotspotsPage({ compact = false }: { compact?: boolean }) {
  const [hotspots, setHotspots] = React.useState<Hotspot[]>([]);
  const [hotspotStatus, setHotspotStatus] = React.useState('正在读取热点...');
  const [hotspotsLoading, setHotspotsLoading] = React.useState(true);
  const [channels, setChannels] = React.useState<HotspotChannel[]>([]);
  const [activeChannelId, setActiveChannelId] = React.useState<number | null>(null);
  const [channelStatus, setChannelStatus] = React.useState('');
  const [sourceStatus, setSourceStatus] = React.useState<HotspotSourceStatus[]>([]);
  const [activeTab, setActiveTab] = React.useState('platform');
  const [collapsed, setCollapsed] = React.useState<Record<string, boolean>>({});

  const loadChannels = React.useCallback(async () => {
    try {
      const data = await getHotspotChannels();
      const items = data.items || [];
      setChannels(items);
      setActiveChannelId((prev) => (prev && items.some((c) => c.id === prev) ? prev : items[0]?.id ?? null));
    } catch (err) {
      setChannelStatus('赛道加载失败，请刷新重试');
    }
  }, []);

  const loadHotspots = React.useCallback(
    async (channelId?: number, force = false) => {
      const cid = channelId ?? activeChannelId;
      if (cid == null) return;
      setHotspotsLoading(true);
      try {
        const data = await getHotspots(cid, 30, force);
        setHotspots(data.items || []);
        setSourceStatus(data.sources || []);
        const failed = (data.sources || []).filter((s) => !s.ok);
        const updated = data.updatedAt
          ? new Date(data.updatedAt).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit' })
          : '';
        if (data.fallback) {
          setHotspotStatus(`热点源不可用：${data.error || '全部源失败'}`);
        } else if (failed.length > 0) {
          setHotspotStatus(`部分源不可用（${failed.map((f) => f.name).join('、')}）· 更新于 ${updated} · 每小时自动刷新`);
        } else {
          setHotspotStatus(`每小时自动刷新 · 更新于 ${updated}`);
        }
      } catch (err) {
        setHotspotStatus('热点读取失败，请稍后刷新');
        setHotspots([]);
      } finally {
        setHotspotsLoading(false);
      }
    },
    [activeChannelId]
  );

  React.useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  React.useEffect(() => {
    if (activeChannelId != null) loadHotspots(activeChannelId);
  }, [activeChannelId, loadHotspots]);

  const categoryOf = (ch: HotspotChannel) => (ch.category === 'field' ? 'field' : ch.category === 'custom' ? 'custom' : 'platform');

  // Tab 列表（custom 有赛道才显示）
  const tabs = React.useMemo(() => {
    const t = [
      { key: 'platform', name: '平台热榜' },
      { key: 'field', name: '行业板块' },
    ];
    if (channels.some((c) => categoryOf(c) === 'custom')) t.push({ key: 'custom', name: '自定义' });
    return t;
  }, [channels]);

  const channelsOfTab = React.useCallback(
    (tab: string) => channels.filter((c) => categoryOf(c) === tab && c.enabled),
    [channels]
  );

  // 分组视图：内置赛道按组映射，未匹配的归「其他」
  const groupChannels = React.useCallback(
    (tab: string): { group: ChannelGroup; items: HotspotChannel[] }[] => {
      const list = channelsOfTab(tab);
      const groupsDef = tab === 'field' ? FIELD_GROUPS : tab === 'platform' ? PLATFORM_GROUPS : [];
      const result: { group: ChannelGroup; items: HotspotChannel[] }[] = [];
      if (groupsDef.length > 0) {
        for (const g of groupsDef) {
          const items = list.filter((c) => g.members.includes(c.name));
          if (items.length > 0) result.push({ group: g, items });
        }
      }
      const known = new Set(result.flatMap((r) => r.items.map((c) => c.name)));
      const rest = list.filter((c) => !known.has(c.name));
      if (rest.length > 0) result.push({ group: { key: 'other', name: '其他', members: [] }, items: rest });
      return result;
    },
    [channelsOfTab]
  );

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    const list = channelsOfTab(tab);
    if (list.length > 0 && !list.some((c) => c.id === activeChannelId)) {
      setActiveChannelId(list[0].id);
    } else if (list.length === 0) {
      setHotspots([]);
    }
  };

  const toggleGroup = (key: string) => {
    setCollapsed((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const maxHot = Math.max(0, ...hotspots.map((h) => h.hot || 0));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>热点</CardTitle>
              <CardDescription>
                {channelStatus || hotspotStatus}
                {sourceStatus.some((s) => !s.ok) ? ' · 源状态见下方' : ''}
              </CardDescription>
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => loadHotspots(activeChannelId ?? undefined, true)}
              disabled={activeChannelId == null}
              title="立即刷新（跳过缓存，实时抓取）"
            >
              <RefreshCw className={`h-4 w-4 ${hotspotsLoading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* 顶部贯通 Tab：平台热榜 / 行业板块（框住下方赛道栏 + 热点列表） */}
          <div className="mb-3 flex items-center gap-1 border-b">
            {tabs.map((t) => {
              const count = channelsOfTab(t.key).length;
              const active = activeTab === t.key;
              return (
                <button
                  key={t.key}
                  onClick={() => handleTabChange(t.key)}
                  className={`-mb-px px-4 py-2 text-[13px] font-medium transition-colors ${
                    active
                      ? 'border-b-2 border-primary text-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {t.name}
                  <span className="ml-1 text-[10px] font-normal text-muted-foreground">{count}</span>
                </button>
              );
            })}
          </div>
          <div className={compact ? 'space-y-3' : 'flex gap-3'}>
            {/* 左侧：分组赛道（管理员在系统设置维护，用户只读） */}
            {compact ? (
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1">
                {groupChannels(activeTab)
                  .flatMap(({ items }) => items)
                  .map((ch) => {
                    const active = activeChannelId === ch.id;
                    const badge = ch.badge ? BADGE_MAP[ch.badge] : null;
                    return (
                      <button
                        key={ch.id}
                        onClick={() => setActiveChannelId(ch.id)}
                        className={`flex flex-shrink-0 items-center gap-1 rounded-full border px-2.5 py-1 text-xs transition-colors ${
                          active
                            ? 'border-primary bg-primary/10 font-medium text-foreground'
                            : 'border-border text-muted-foreground hover:bg-accent/40'
                        }`}
                        title={ch.name}
                      >
                        <span className="max-w-[72px] truncate">{ch.name}</span>
                        {badge && (
                          <span
                            className={`rounded border px-1 text-[9px] font-bold leading-[14px] ${badge.className}`}
                          >
                            {badge.label}
                          </span>
                        )}
                      </button>
                    );
                  })}
              </div>
            ) : (
            <div className="w-56 flex-shrink-0 border-r pr-3">
              <div className="max-h-[65vh] space-y-2.5 overflow-y-auto pr-1">
                {channels.length === 0 && !channelStatus && (
                  <p className="px-2 py-3 text-xs text-muted-foreground">正在加载赛道...</p>
                )}
                {groupChannels(activeTab).map(({ group, items }) => {
                  const foldKey = `${activeTab}:${group.key}`;
                  const isCollapsed = !!collapsed[foldKey];
                  return (
                    <div key={foldKey}>
                      <button
                        onClick={() => toggleGroup(foldKey)}
                        className="flex w-full items-center gap-1.5 border-l-2 border-primary px-1.5 py-1 text-left text-[11px] font-bold tracking-wide text-primary"
                      >
                        <span className="w-2.5 text-[9px] opacity-70">{isCollapsed ? '›' : '∨'}</span>
                        <span>{group.name}</span>
                        <span className="ml-auto pr-0.5 text-[10px] font-normal text-muted-foreground">{items.length}</span>
                      </button>
                      {!isCollapsed &&
                        items.map((ch) => {
                          const active = activeChannelId === ch.id;
                          const badge = ch.badge ? BADGE_MAP[ch.badge] : null;
                          return (
                            <button
                              key={ch.id}
                              onClick={() => setActiveChannelId(ch.id)}
                              className={`mt-0.5 flex w-full items-center gap-1 rounded px-2 py-1.5 text-left text-sm transition-colors ${
                                active
                                  ? 'bg-primary/10 font-medium text-foreground'
                                  : 'text-muted-foreground hover:bg-accent/40'
                              }`}
                              title={ch.name}
                            >
                              <span className="min-w-0 flex-1 truncate">{ch.name}</span>
                              {badge && (
                                <span
                                  className={`flex-shrink-0 rounded border px-1 text-[9px] font-bold leading-[14px] ${badge.className}`}
                                >
                                  {badge.label}
                                </span>
                              )}
                            </button>
                          );
                        })}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {/* 右侧：热点列表（弹性高度，按热度排序，热度条 + 升降箭头） */}
            <div className={compact ? 'min-w-0' : 'min-w-0 flex-1 pl-3'}>
              {hotspotsLoading ? (
                <div className="space-y-3">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <Skeleton key={i} className="h-14 rounded" />
                  ))}
                </div>
              ) : hotspots.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-8 text-muted-foreground">
                  <Flame className="mb-2 h-6 w-6" />
                  <p className="text-sm">暂无热点数据</p>
                  {sourceStatus.length > 0 && (
                    <div className="mt-3 w-full max-w-md space-y-1 text-xs">
                      {sourceStatus.map((s) => (
                        <div key={s.url} className="flex items-center justify-between rounded border px-2 py-1">
                          <span className="truncate">{s.name}</span>
                          <span className={s.ok ? 'text-green-600' : 'text-destructive'}>
                            {s.ok ? '正常' : s.error?.slice(0, 40) || '失败'}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="divide-y">
                  {hotspots.map((item, i) => (
                    <a
                      key={`${item.title}-${i}`}
                      href={item.url}
                      target="_blank"
                      rel="noreferrer"
                      className="-mx-2 flex items-start gap-3 rounded px-2 py-3 transition-colors hover:bg-accent/40"
                    >
                      <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-bold text-primary">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium line-clamp-2">{item.title}</div>
                        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-[10px]">{item.source}</Badge>
                          {item.pub && <span>{item.pub}</span>}
                          <ExternalLink className="h-3 w-3" />
                        </div>
                      </div>
                      <div className="flex flex-shrink-0 flex-col items-end justify-center gap-1">
                        {formatHot(item.hot) && (
                          <span className="text-sm font-bold tabular-nums text-foreground">
                            {formatHot(item.hot)}
                          </span>
                        )}
                        <div className="flex items-center gap-2">
                          <HotBar hot={item.hot || 0} maxHot={maxHot} />
                          <TrendArrow trend={item.trend} />
                        </div>
                      </div>
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
